import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import api from "../../../../services/api";
import TemplateDialog from "./TemplateDialog";
import { useBlockLayout, useFlexLayout, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import Grid from "../../../Grid";
import { Checkbox, RadioButton } from "@progress/kendo-react-inputs";

function ClientExclusionDialog(props) {
    const {
        title,
        selectedCandidates,
        selectedCandidateId,
        initialSelectedClients,
        onClose,
        onBackClick,
        onNextClick,
        direction,
    } = props;

    const [ loading, setLoading ] = useState(false);
    const [ sourceData, setSourceData ] = useState([]);
    const [ data, setData ] = useState([]);
    const [ selectedClients, setSelectedClients ] = useState(initialSelectedClients ?? []);
    const skipPageResetRef = useRef();

    const loadData = useCallback(() => {
        skipPageResetRef.current = true;

        if (!selectedCandidates?.length)
            return;

        setLoading(true);

        api.post(`candidatemerge/compare/clients/exclusion`, JSON.stringify(selectedCandidates.map(c => c.candidateId)))
            .then((response) => {
                const {data: {data: responseData, maxRowCount}} = response;

                if (!responseData.length)
                {
                    setLoading(false);

                    if (direction === 1)
                        onNextClick([]);
                    else
                        onBackClick();

                    return;
                }

                const transposedData = [];
                // console.log({responseData});

                for (let i = 0; i < maxRowCount; i++) {
                    const row = {};

                    responseData.forEach(d => {
                        if (responseData.filter(r => r.candidateId === d.candidateId).length - 1 < i)
                            return;

                        const currentResponseData = responseData.filter(r => r.candidateId === d.candidateId)[i];
                        row[d.candidateId] = {};
                        row[d.candidateId].candidateId = currentResponseData.candidateId;
                        row[d.candidateId].candidateClientExclusionId = currentResponseData.candidateClientExclusionId;
                        row[d.candidateId].clientId = currentResponseData.clientId;
                        row[d.candidateId].clientName = currentResponseData.clientName;
                    });

                    if (!Object.keys(row).length)
                        continue;

                    transposedData.push(row);
                }
                // console.log({transposedData});
                setData([...transposedData]);
                setSourceData([...transposedData]);
                setLoading(false);
            })
            .catch((error) => {
                console.log({error});
                setLoading(false);
            })
        ;
    }, [skipPageResetRef, api, selectedCandidateId, onNextClick, onBackClick, direction]);

    useEffect(() => loadData(), [selectedCandidates]);

    const defaultColumn = useMemo(() => ({
        disableFilters: true,
        disableSortBy: true,
    }), []);

    const columns = useMemo(() => {
        selectedCandidates.sort((a, b) => a.candidateId - selectedCandidateId !== 0 ? Math.abs(a.candidateId - b.candidateId) : -1);

        return [
            ...selectedCandidates.map(d => ({
                Header: () => {
                    const currentClients = sourceData?.filter(sd => sd[d.candidateId]?.candidateId === d.candidateId).map(sd => sd[d.candidateId]?.clientId);
                    const selected = currentClients?.length && currentClients.every(sd => selectedClients.includes(sd)) ? true : false;

                    return (
                        <div className="w-100 px-1 d-flex flex-column justify-content-start align-items-start">
                            <div className="w-100 d-flex flex-row justify-content-start align-items-center">
                            {
                                selectedCandidateId === d.candidateId &&
                                <RadioButton
                                    name="selectCandidate"
                                    value={d.candidateId}
                                    disabled={true}
                                    checked={d.candidateId === selectedCandidateId}
                                />
                            }
                                <span className={`text-left${selectedCandidateId === d.candidateId ? " ml-2" : ""}`}>{d.candidateName} ({d.candidateId})</span>
                            </div>
                            <div className="w-100 d-flex flex-row justify-content-start align-items-center mt-2">
                                <Checkbox
                                    onChange={(e) => {
                                        const {value} = e;

                                        if (!value)
                                        {
                                            setSelectedClients([...selectedClients.filter(sd => !currentClients.includes(sd))]);
                                            return;
                                        }

                                        setSelectedClients([...selectedClients.filter(sd => !currentClients.includes(sd)), ...currentClients]);
                                    }}
                                    checked={selected}
                                />
                                <span className="text-left ml-2">Select All</span>
                            </div>
                        </div>
                    );
                },
                id: `exclusion_${d.candidateId}`,
                accessor: `${d.candidateId}.clientName`,
                Cell: ({ row: { original } }) => {
                    return (
                        <div className="w-100 px-1 d-flex flex-row justify-content-start align-items-start">
                        {
                            original[d.candidateId] ?
                            <>
                                <Checkbox
                                    onChange={(e) => {
                                        const {value} = e;

                                        if (!value)
                                        {
                                            setSelectedClients([...selectedClients.filter(sc => sc !== original[d.candidateId].clientId)]);
                                            return;
                                        }

                                        setSelectedClients([...selectedClients, original[d.candidateId].clientId]);
                                    }}
                                    checked={selectedClients.includes(original[d.candidateId].clientId)}
                                />
                                <span className="text-left ml-2">{original[d.candidateId].clientName}</span>
                            </>
                            :
                            <span className="text-left ml-2">-</span>
                        }
                        </div>
                    );
                },
                minWidth: 200,
                sticky: d.candidateId === selectedCandidateId ? "left" : null,
            }))
        ]
    }, [selectedCandidates, selectedCandidateId, sourceData, selectedClients]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data,
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            autoResetSortBy: !skipPageResetRef.current,
            autoResetFilters: !skipPageResetRef.current,
            autoResetRowState: !skipPageResetRef.current,
            defaultColumn,
        },
        useFlexLayout,
        useBlockLayout,
        useSticky,
    );

    // useEffect(() => console.log({rows}), [rows]);

    const content = useMemo(() => {
        return (
            <>
                <Row className="m-0 p-0 w-100">
                    <Col lg={12} className="m-0 p-0 d-flex flex-wrap justify-content-start align-items-center">
                        <span className="font-size-small-custom">
                            <i className="fas fa-info-circle mr-1" />Please select the Client Exclusion to be merged.
                        </span>
                    </Col>
                </Row>
                <Row className="m-0 p-0 mt-3 w-100">
                    <Grid
                        height={'calc(100vh - 464px)'}
                        totalRecords={rows.length}
                        pageSize={rows.length}
                        rows={rows}
                        tableProps={getTableProps()}
                        headerGroups={headerGroups.map(h => {
                            const headerGroupProps = h.getHeaderGroupProps();
                            headerGroupProps.style = {...headerGroupProps.style, width: "100%"}

                            return {
                                ...h,
                                getHeaderGroupProps: () => headerGroupProps
                            };
                        })}
                        tableBodyProps={getTableBodyProps()}
                        prepareRow={(row) => {
                            prepareRow(row);
                            const rowProps = row.getRowProps();
                            row.getRowProps = () => {
                                return {
                                    ...rowProps,
                                    style: {
                                        ...rowProps.style,
                                        width: "100%",
                                    },
                                };
                            };

                            row.cells = row.cells.map(c => {
                                const cellProps = c.getCellProps();
                                return {
                                    ...c,
                                    getCellProps: () => ({
                                        ...cellProps,
                                        style: {
                                            ...cellProps.style,
                                            backgroundColor: c.column.id.includes(`${selectedCandidateId}`) ? "#c2deff" : c.column.id === "title" ? "#fff" : "inherit",
                                        }
                                    })
                                };
                            });
                        }}
                        fullscreen={true}
                    />
                </Row>
            </>
        );
    }, [rows, getTableProps, headerGroups, getTableBodyProps, prepareRow, selectedCandidateId]);

    const footer = useMemo(() => (
        <>
            <Button
                color="secondary"
                onClick={onClose}
                type="button"
            >
                Close
            </Button>
            <Button
                color="info"
                onClick={() => onBackClick()}
                type="button"
            >
                <i className="fas fa-chevron-left mr-2" />Back: Data Compare - Client Inclusion
            </Button>
            <Button
                color="info"
                onClick={() => onNextClick(selectedClients)}
                type="button"
            >
                Next: Data Compare - Emergency Contact<i className="fas fa-chevron-right ml-2" />
            </Button>
        </>
    ), [onClose, onBackClick, onNextClick, selectedClients]);

    return (
        <TemplateDialog
            title={title}
            loading={loading}
            content={content}
            footer={footer}
            onClose={onClose}
        />
    );
};

export default ClientExclusionDialog;
